import { IQueryResponse } from '@services/api';
import { ClubDetails, MoreDetails } from '../enduser-details';
import { useCheckRxdb } from '@hooks/checkRxdb';
import { APIService } from '@services/api/common-services';
import { APICONFIG } from '@config/api.config';
import { useQuery } from '@tanstack/react-query';
import { setRxdb } from '@helper/utils';
import { useUserRoleType } from '@hooks/useUserRoleType';

/**
 * Map `business_detail` club fields to the dropdown title (club list comes from `club-dropdown`).
 */
export const resolveClubTitleFromBusinessDetail = (detail: MoreDetails, clubs: ClubDetails[]): string => {
	const fromName = detail.clubname_c?.trim();
	if (fromName) {
		return fromName;
	}
	const mapping = detail.club_mapping;
	if (mapping == null || mapping === ('' as unknown as number)) {
		return '';
	}
	const asNum = typeof mapping === 'number' ? mapping : Number(mapping);
	if (!Number.isNaN(asNum)) {
		const byId = clubs.find((c) => c.id === asNum);
		if (byId?.name) {
			return byId.name;
		}
	}
	const key = String(mapping).trim();
	const bySfid = clubs.find((c) => c.sfid === key || c.clubid === key);
	return bySfid?.name ?? '';
};

/**
 * @param is_ob_meet — meeting flow; when true, response is not written to RxDB `club`.
 * @param memberFormLeadId — when set (lead/account id on More Details), always calls `club-dropdown` for that screen even if RxDB already has clubs.
 */
export const useClubDropDownQuery = (
	is_ob_meet: boolean = false,
	memberFormLeadId?: number | null
): IQueryResponse<Array<ClubDetails>> => {
	const { isDbChecked, shouldFetch } = useCheckRxdb('club');
	const userRole = useUserRoleType();
	const isRBDM = userRole === 'RBDM';
	const apiEndpoint = isRBDM ? APICONFIG.RBDM_CLUB_DROPDOWN : APICONFIG.CLUB_DROPDOWN;
	const fetchForMemberMoreDetails =
		memberFormLeadId != null && memberFormLeadId !== 0 && Number.isFinite(Number(memberFormLeadId));

	const fetchClubListData = async (): Promise<Array<ClubDetails>> => {
		const payload: Record<string, string> = {};
		if (isRBDM) {
			payload.limit = '0';
		} else {
			payload.limit = '0';
			payload.is_ob_meet = String(is_ob_meet);
		}

		const response = await APIService.getData<Array<ClubDetails>>(apiEndpoint, payload);
		if (response && !is_ob_meet) {
			const dbCollection = isRBDM ? 'rbdm_club' : 'club';
			await setRxdb(dbCollection, response);
		}
		return response;
	};

	const queryEnabled = fetchForMemberMoreDetails ? isDbChecked : isDbChecked && shouldFetch;

	const { data, error, isLoading, isPending, refetch, isFetching } = useQuery({
		queryKey: fetchForMemberMoreDetails
			? ['clubList', userRole, 'member-more-details', memberFormLeadId]
			: ['clubList', userRole],
		queryFn: fetchClubListData,
		enabled: queryEnabled,
		retry: false,
		staleTime: fetchForMemberMoreDetails ? 0 : 30 * 60 * 1000,
		refetchOnWindowFocus: false,
		refetchOnMount: fetchForMemberMoreDetails ? 'always' : false,
		refetchOnReconnect: false,
	});

	return {
		data,
		error,
		isLoading,
		isPending,
		refetch,
		isFetching,
	};
};
